/*------------------------------------------------------------------------------
   About      : Sparse update payload for an insight (epic DA-697, II-FE-3)

                `updateAll` is diff-then-patch: a row carries ONLY the fields
                that changed. Three rules from the contract drive everything
                here, and getting any of them wrong fails quietly:

                1. **`code`, `insightId` and `editStatus` are server-owned.**
                   quill rejects a client-supplied value rather than ignoring
                   it, so one stale field fails the whole row. They are excluded
                   by construction below, not by remembering to delete them —
                   the same reasoning as the `stateMapperIsDerived` guard in
                   `SurveyQuestionForm.tsx`.

                2. **JSON blobs are merged server-side, never replaced.** For
                   `groupInfo`, `templateCriteria`, `excludeCriteria` and
                   `validationState` the server merges what it receives into
                   what it stores, so a partial object leaves untouched keys
                   alive. Send the whole object or nothing.

                3. **Validation runs against the merged record**, not the sparse
                   patch, so omitting an unchanged field is safe and does not
                   read to the server as "cleared".

                Modelled on `SurveyQBUtils.getQuestionDiff` (strips Strapi
                component ids, excludes server-derived fields) rather than
                `RecoUtils.getRecoDiff`, which has neither.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { isEqual, omit }        from 'lodash'
import { InsightCreateData,
         InsightRecord,
         InsightUpdatePatch }   from '../types/insightTypes'

/**
 * Fields the client may send. Anything absent from this list can never reach
 * the server, which is what makes rule 1 structural rather than a convention
 * someone has to remember.
 */
export const EDITABLE_INSIGHT_FIELDS = [
  'type', 'validityType', 'timeOfUse', 'applianceId', 'measurementType',
  'direction', 'sentimentType', 'category', 'groupInfo',
  'pickUnitType', 'pickUnitCount', 'compareUnitType', 'compareUnitCount',
  'gap', 'benchmarkPercentile',
  'templateString', 'templateExample', 'alternativeVersions',
  'templateCriteria', 'excludeCriteria', 'link', 'disabled',
  /* Client-owned since quill stopped computing them: it stores what the save
     carries, verbatim, and REPLACES the object wholesale rather than merging.
     So they must not be in MERGED_BLOB_FIELDS — a merge would resurrect slots
     the client meant to clear, restoring a stale green dot. */
  'validationState', 'lastValidation',
] as const satisfies readonly (keyof InsightCreateData)[]

export type EditableInsightField = typeof EDITABLE_INSIGHT_FIELDS[number]

/**
 * Server-owned. Listed explicitly so the exclusion is greppable and so a test
 * can assert on the list rather than on one example field.
 *
 * Mirrors quill's own `SERVER_OWNED_INSIGHT_FIELDS`, which **rejects** any of
 * these if a client sends them — loudly, not silently.
 *
 * `validationState` and `lastValidation` used to be here. They are now
 * client-owned: quill neither computes nor second-guesses the three slots, it
 * persists what the save carries and serves it back.
 *
 * (`templateId` is ours: quill emits it as `= insightId` and never accepts it.)
 */
export const SERVER_OWNED_INSIGHT_FIELDS = [
  'code', 'insightId', 'editStatus', 'templateId',
] as const

/**
 * Fields whose stored value is MERGED with what we send.
 *
 * Consequence: emitting a partial object here silently keeps keys the user
 * removed. So when one of these changes at all, the whole object goes.
 */
const MERGED_BLOB_FIELDS = new Set<EditableInsightField>([
  // Exactly quill's `JSON_MERGE_FIELDS`. `excludeCriteria` is NOT here — quill
  // treats it as a scalar and replaces it, so sending `{}` to clear it (as the
  // merged path does) would store an empty object where null was meant.
  'groupInfo', 'templateCriteria',
])

/**
 * Strapi returns component objects carrying an `id` the client never authors.
 * Comparing it would diff every record as modified the moment it loads — the
 * exact bug `getQuestionDiff` strips ids to avoid.
 */
function stripComponentIds(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripComponentIds)
  if (value && typeof value === 'object') return omit(value as Record<string, unknown>, 'id')
  return value
}

/**
 * Treats null, undefined and '' as the same absence.
 *
 * Without this, a field the user never touched diffs as changed the first time
 * a form control coerces an absent value into an empty string — which
 * react-hook-form does routinely for text and select inputs.
 */
function normalize(value: unknown): unknown {
  if (value === null || value === undefined || value === '') return undefined
  return stripComponentIds(value)
}

/** True when the two values differ in a way worth sending. */
export function fieldChanged(next: unknown, prev: unknown): boolean {
  return !isEqual(normalize(next), normalize(prev))
}

/**
 * Builds the sparse patch for one insight.
 *
 * Returns only `{ documentId }` when nothing changed — the caller should treat
 * a single-key result as "no-op" and skip the row rather than sending it, so an
 * unchanged record never bumps a cache timestamp or demotes
 * `PUBLISHED → MODIFIED` for no reason.
 */
export function buildInsightPatch(
  documentId : string,
  next       : InsightCreateData,
  prev       : Partial<InsightRecord>,
): InsightUpdatePatch {

  const patch : InsightUpdatePatch = { documentId }

  for (const field of EDITABLE_INSIGHT_FIELDS) {
    const nextValue = (next as Record<string, unknown>)[field]

    // A field the form did not supply at all is not an edit to it. This is the
    // difference between "the user cleared this" (null, which we do send) and
    // "this form does not manage this field" (undefined, which we do not).
    if (nextValue === undefined && !(field in next)) continue

    const prevValue = (prev as Record<string, unknown>)[field]
    if (!fieldChanged(nextValue, prevValue)) continue

    // Merged blobs go whole — see MERGED_BLOB_FIELDS.
    ;(patch as Record<string, unknown>)[field] = MERGED_BLOB_FIELDS.has(field)
      ? (nextValue ?? {})
      : nextValue
  }

  return patch
}

/** True when the patch carries no actual edit. */
export function isNoOpPatch(patch: InsightUpdatePatch): boolean {
  return Object.keys(patch).length <= 1
}

/**
 * Which editable fields differ between the working copy and the published
 * version — drives the per-field "modified" highlight.
 *
 * Deliberately compares against the PUBLISHED record rather than the load-time
 * draft: the highlight answers "what does this change relative to what is
 * live", which is the question a reviewer has. Comparing to the draft would
 * highlight nothing on a freshly loaded modified record, which is precisely
 * when the reviewer needs it.
 */
export function modifiedFields(
  working   : InsightCreateData,
  published : Partial<InsightRecord> | undefined,
): Set<EditableInsightField> {
  const modified = new Set<EditableInsightField>()
  if (!published) return modified

  for (const field of EDITABLE_INSIGHT_FIELDS) {
    const nextValue = (working as Record<string, unknown>)[field]
    if (nextValue === undefined && !(field in working)) continue
    if (fieldChanged(nextValue, (published as Record<string, unknown>)[field])) modified.add(field)
  }
  return modified
}

/**
 * Reduces a full record to just the editable fields, for seeding a form.
 *
 * Using this rather than spreading the record keeps server-owned fields out of
 * form state entirely, so they cannot be round-tripped back by accident.
 */
export function toEditableData(record: Partial<InsightRecord>): InsightCreateData {
  const data : Record<string, unknown> = {}
  for (const field of EDITABLE_INSIGHT_FIELDS) {
    const value = (record as Record<string, unknown>)[field]
    if (value !== undefined) data[field] = value
  }
  return data as InsightCreateData
}
